// Minimal fetch wrapper. Same-origin /api/* (Caddy proxies to cloud-api:8089).
// Handles 401 → portal login redirect; 4xx/5xx → throws ApiError with body.

import type {
  AggressiveStatusResponse,
  Tier1Result,
  Tour,
  TourVocabularyEntry,
  Venue,
} from "@/lib/types";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API error ${status}`);
    this.status = status;
    this.body = body;
  }
}

type FetchOpts = RequestInit & { json?: unknown; skipAuthRedirect?: boolean };

async function request<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const { json, skipAuthRedirect, headers, ...rest } = opts;
  const init: RequestInit = {
    ...rest,
    headers: {
      "Accept": "application/json",
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    credentials: "include", // send ovr_portal_session cookie
  };
  if (json !== undefined) init.body = JSON.stringify(json);

  const resp = await fetch(path, init);

  if (resp.status === 401 && !skipAuthRedirect) {
    // Clients need to log in via the portal flow; admins get CF Access challenge
    // at the edge and never hit this. Redirect preserves current URL.
    const ret = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/portal/login?return=${ret}`;
    return new Promise(() => {}); // never resolves; page unloads
  }

  const contentType = resp.headers.get("content-type") ?? "";
  const body = contentType.includes("application/json") ? await resp.json() : await resp.text();

  if (!resp.ok) throw new ApiError(resp.status, body);
  return body as T;
}

export const api = {
  get: <T>(path: string, opts?: FetchOpts) => request<T>(path, { ...opts, method: "GET" }),
  post: <T>(path: string, json?: unknown, opts?: FetchOpts) =>
    request<T>(path, { ...opts, method: "POST", json }),
  patch: <T>(path: string, json?: unknown, opts?: FetchOpts) =>
    request<T>(path, { ...opts, method: "PATCH", json }),
  delete: <T>(path: string, opts?: FetchOpts) => request<T>(path, { ...opts, method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Roster spatial-first tours (U5) — typed helpers for the AdminLoadIn dropdown
// + VocabularyInlineAddDialog. These wrap the U1/U3 endpoints with the exact
// request/response shapes shipped by cloud/services/api/routes/tours.py.
// ---------------------------------------------------------------------------

/** PATCH /api/memberships/{id}/touring-identity — set or clear the tag.
 *  Backend validates that `identity` exists in the window's tour vocabulary
 *  when non-null; throws ApiError(400) with `identity_not_in_tour_vocabulary`
 *  if the operator picked a stale option mid-edit. */
export function setTouringIdentity(
  membershipId: string,
  identity: string | null,
): Promise<unknown> {
  return api.patch(
    `/api/memberships/${encodeURIComponent(membershipId)}/touring-identity`,
    { touring_identity: identity },
  );
}

/** GET /api/tours/{tourId}/suggest-identity?service_name=… — derived
 *  most-recent tagging on this tour for the same service_name. Returns
 *  `{ touring_identity: null }` when no prior tagging exists. */
export function suggestTouringIdentity(
  tourId: string,
  serviceName: string,
): Promise<{ touring_identity: string | null }> {
  return api.get<{ touring_identity: string | null }>(
    `/api/tours/${encodeURIComponent(tourId)}/suggest-identity`
      + `?service_name=${encodeURIComponent(serviceName)}`,
  );
}

/** GET /api/tours/{tourId} — fetches a single tour including its vocabulary. */
export function getTour(tourId: string): Promise<Tour> {
  return api.get<Tour>(`/api/tours/${encodeURIComponent(tourId)}`);
}

/** GET /api/tours — list all active tours (admin-only). Used by the F4
 *  TourAssignmentDialog tour picker (U8) and any future tour browser. */
export function getTours(): Promise<{ tours: Tour[]; count: number }> {
  return api.get<{ tours: Tour[]; count: number }>("/api/tours");
}

/** POST /api/tours — create a new tour. Used by the F4 dialog's "+ Create
 *  new tour" inline affordance when the tour list is empty or the operator
 *  wants to make a fresh one mid-assignment. */
export function createTour(body: {
  name: string;
  artist?: string;
}): Promise<Tour> {
  return api.post<Tour>("/api/tours", body);
}

/** PATCH /api/windows/{id} with intent="assign_tour" — atomically promotes
 *  the event's distinct service_location_label values into the tour's
 *  vocabulary (with operator-confirmed tier per label), tags each open
 *  membership's touring_identity, and sets windows.tour_id. See plan U8.
 *
 *  tier_map must have an entry for every distinct non-NULL
 *  service_location_label on the event's open memberships. Server rejects
 *  with `tier_map_incomplete` (400) listing missing labels otherwise.
 *
 *  Caller-visible errors:
 *    - 400 nothing_to_migrate    — no service has a label tagged yet
 *    - 400 tier_map_incomplete   — body.missing[] names the gaps
 *    - 400 invalid_tier          — value outside {stage, house}
 *    - 400 tour_not_found / tour_deleted
 *    - 409 tier_conflict         — body.conflicts[] has per-label
 *                                  {label, existing_tier, requested_tier}
 */
export function assignTourToWindow(
  windowId: string,
  tour_id: string,
  tier_map: Record<string, "stage" | "house">,
): Promise<{
  tour_id: string;
  window_id: string;
  vocabulary_added: Array<{ label: string; tier: "stage" | "house" }>;
  memberships_tagged: number;
}> {
  return api.patch(
    `/api/windows/${encodeURIComponent(windowId)}`,
    { intent: "assign_tour", tour_id, tier_map },
  );
}

/** Read-then-PATCH atomic vocabulary append. The PATCH on /api/tours/{id}
 *  uses full-replace semantics on `touring_service_vocabulary`, so we
 *  fetch current vocab → append the new entry → send the full array.
 *
 *  Caller-visible errors:
 *    - ApiError(409) `tour_name_conflict` / `duplicate vocabulary label …`
 *    - ApiError(400) on structural validation problems (label too long,
 *      invalid tier, disallowed chars, etc.)
 *
 *  Note: there's an inherent TOCTOU race here (two operators appending
 *  concurrently can clobber each other) but the server's case-insensitive
 *  uniqueness check still catches duplicate labels — worst case is one
 *  operator's submit lands a 409 and they retry. v1 accepts this; a future
 *  iteration could add an explicit append-vocabulary endpoint.
 */
export async function addTourVocabularyEntry(
  tourId: string,
  entry: TourVocabularyEntry,
): Promise<Tour> {
  const current = await getTour(tourId);
  const next: TourVocabularyEntry[] = [
    ...current.touring_service_vocabulary,
    entry,
  ];
  return api.patch<Tour>(
    `/api/tours/${encodeURIComponent(tourId)}`,
    { touring_service_vocabulary: next },
  );
}

// ---------------------------------------------------------------------------
// Tier 1 subset-coincidence (U5 rewrite) — manual subset preview + status poll
// ---------------------------------------------------------------------------

export interface SubsetCoincidenceRequest {
  membership_ids: string[];
  /** Aggressive transient ratio (0..1.5). Defaults to gate config when omitted. */
  transient_ratio?: number;
  /** Aggressive continuous ratio (0..1.5). Defaults to gate config when omitted. */
  continuous_ratio?: number;
  /** Feed rating in amps for verdict computation. Verdict is null when omitted. */
  feed_rating_a?: number;
  /** Source-selection knob. "best" = materialized → 10s → fallback. */
  source_preference?: "best" | "materialized_60s" | "cloud_vm_dyno" | "fallback";
}

/** POST /api/windows/{window_id}/roster/subset-coincidence — admin-only.
 *  Returns the full Tier 1 result (coincidence + continuous + verdict +
 *  4-source provenance + onset alignment + missing_curves). Accepts an
 *  AbortSignal for cancellation when the operator drags the ratio slider
 *  in flight. */
export function subsetCoincidence(
  windowId: string,
  body: SubsetCoincidenceRequest,
  opts?: { signal?: AbortSignal },
): Promise<Tier1Result> {
  return api.post<Tier1Result>(
    `/api/windows/${encodeURIComponent(windowId)}/roster/subset-coincidence`,
    body,
    { signal: opts?.signal },
  );
}

// ---------------------------------------------------------------------------
// Show-start override (roster-infra U4) — operator-facing PATCH for the
// `actual_show_start_ts` carve-out on `event_show_periods`. Setting null
// clears the override and re-enables auto-detect on the next roster
// materialization. Backend validates ts ∈ [doors_at, doors_at + 24h]; a
// 400 surfaces via ApiError so the dialog can render it inline.
// ---------------------------------------------------------------------------

export interface ShowStartOverrideResponse {
  period_id: string;
  window_id: string;
  actual_show_start_ts: string | null;
  curve_rows_updated: number;
}

export function setShowStartOverride(
  periodId: string,
  ts: string | null,
): Promise<ShowStartOverrideResponse> {
  return api.patch<ShowStartOverrideResponse>(
    `/api/show-periods/${encodeURIComponent(periodId)}/show-start-override`,
    { actual_show_start_ts: ts },
  );
}

/** GET /api/feed-suggestions/aggressive-status/{job_id} — admin-only.
 *  Polling endpoint for the streaming Aggressive Tier 1 worker. Returns 404
 *  once the 5-minute TTL has fired (job expired). */
export function getAggressiveStatus(
  jobId: string,
  opts?: { signal?: AbortSignal },
): Promise<AggressiveStatusResponse> {
  return api.get<AggressiveStatusResponse>(
    `/api/feed-suggestions/aggressive-status/${encodeURIComponent(jobId)}`,
    { signal: opts?.signal },
  );
}

// ---------------------------------------------------------------------------
// Venue-aware planner helpers (next-event load-in planning per the
// roster-infra-delta plan's follow-up — venue inventory + proximity-aware
// balanced bin-pack instead of abstract-amperage enumeration).
// ---------------------------------------------------------------------------

export interface VenueHouseFeed {
  feed_id: string;
  venue_id: string;
  feed_name: string;
  capacity_amps: number;
  location_label: string | null;
  connector_type: string | null;
  notes: string | null;
  // Operator-curated touring-vocabulary identities this feed serves at
  // this venue (e.g., ["foh", "monitors"] on an arena's FOH/Mix feed).
  // Drives the planner's 'assigned' tier — partitions whose
  // canonicalized touring_identity is in this list short-circuit to the
  // strongest match. Always present after migration; defaults to [].
  served_identities: string[];
  deleted_at: string | null;
}

export interface TouringVocabIdentity {
  label: string;
  tier: string;
}
export interface ListTouringVocabularyResponse {
  identities: TouringVocabIdentity[];
}
export function getTouringVocabulary(): Promise<ListTouringVocabularyResponse> {
  return api.get<ListTouringVocabularyResponse>("/api/touring-vocabulary");
}

export interface ListVenuesResponse {
  venues: Venue[];
  count: number;
}

export interface ListVenueHouseFeedsResponse {
  feeds: VenueHouseFeed[];
  count: number;
}

export function listVenues(): Promise<ListVenuesResponse> {
  return api.get<ListVenuesResponse>("/api/venues");
}

export function getVenueHouseFeeds(
  venueId: string,
): Promise<ListVenueHouseFeedsResponse> {
  return api.get<ListVenueHouseFeedsResponse>(
    `/api/venues/${encodeURIComponent(venueId)}/house-feeds`,
  );
}

// ---------------------------------------------------------------------------
// Tier 2 feed-detail — drill-down stats for a specific feed assignment.
// Sourced from cloud-victoria-metrics :10s_max series; identical to edge VM
// 200ms native for peak detection (validated empirically on AG-Oakland).
// ---------------------------------------------------------------------------

export interface FeedDetailSummary {
  peak_a: number | null;
  p99: number | null;
  p95: number | null;
  p50: number | null;
  avg: number | null;
  duration_s: number;
}

export interface FeedDetailThresholdCrossing {
  threshold_a: number;
  seconds_above: number;
}

export interface FeedDetailHistogramBucket {
  min_a: number;
  max_a: number;
  count: number;
}

/** Per-period summary returned alongside the aggregated drill-down stats.
 *  When the backend iterates all completed periods (source ends with
 *  "_lifetime"), this array has one entry per contributing period; one
 *  entry is flagged as the chart_period (the period whose time_series the
 *  response carries). */
export interface FeedDetailPeriodSummary {
  period_id: string;
  started_at_ns: number;
  ended_at_ns: number;
  peak_a: number | null;
  is_chart_period: boolean;
}

export interface FeedDetailResponse {
  window_id: string;
  period_id: string | null;
  feed_rating_a: number;
  system_ids: string[];
  /** "cloud_vm_10s_max_lifetime" when aggregated across every completed
   *  declared period (default behavior, matches show_condition_max_a
   *  semantics). "cloud_vm_10s_max" when a single period was requested
   *  via period_id. "unavailable" when no telemetry was returned. */
  source: "cloud_vm_10s_max" | "cloud_vm_10s_max_lifetime" | "unavailable";
  summary: FeedDetailSummary | null;
  time_series: Array<[number, number, number | null]>; // [ts_ns, peak, avg]
  threshold_crossings: Record<string, FeedDetailThresholdCrossing>;
  histogram: FeedDetailHistogramBucket[];
  /** Optional: list of every completed declared period contributing to the
   *  aggregated summary. Absent when the response came from the legacy
   *  single-period code path. */
  periods_included?: FeedDetailPeriodSummary[];
}

export function getFeedDetail(
  windowId: string,
  body: {
    membership_ids: string[];
    feed_rating_a: number;
    period_id?: string;
  },
  opts?: { signal?: AbortSignal },
): Promise<FeedDetailResponse> {
  return api.post<FeedDetailResponse>(
    `/api/windows/${encodeURIComponent(windowId)}/roster/feed-detail`,
    body,
    { signal: opts?.signal },
  );
}
